/**
 * Reports.
 *
 * The generation lives on the server (modules/reports); this screen is only
 * the way to ask for it. Two things are worth knowing before changing it:
 *
 *   * The list of reports comes from GET /api/reports rather than being
 *     hard-coded, so what the server will produce and what the page offers can
 *     never drift apart — and the channel report appears only for the tiers
 *     the server says may have it.
 *   * A download cannot be a plain <a href>. The access token lives in memory
 *     and travels in an Authorization header, which a navigation does not
 *     send. So the file is fetched with that header, held as a Blob, and
 *     handed to the browser through a short-lived object URL (see download()).
 */

import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, qs, getAccessToken, ApiError } from '../lib/api';
import { useApiError } from '../lib/useApiError';
import { Card, Button, Skeleton, ErrorNotice, EmptyState, Spinner } from '../components/ui';
import { IconFramework } from '../components/icons';

type Format = 'pdf' | 'csv';

interface ReportInfo {
  key: string;
  name: string;
  description: string;
  formats: Format[];
}

interface FrameworkInfo {
  key: string;
  name: string;
  version: string | null;
  licensed: boolean;
}

// ---------------------------------------------------------------------------
//  Authenticated download
// ---------------------------------------------------------------------------

/** Extract `filename="..."` from a Content-Disposition header, if present. */
function filenameFrom(header: string | null, fallback: string): string {
  const match = header?.match(/filename="?([^";]+)"?/i);
  // Strip any path component: the name comes from a response header and is
  // used as a download name, and a name like `../x` should never be honoured.
  return match?.[1]?.split(/[\\/]/).pop() || fallback;
}

async function fetchReport(path: string, signal: AbortSignal): Promise<Response> {
  const send = () =>
    fetch(`/api${path}`, {
      headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      credentials: 'include',
      signal,
    });

  let response = await send();
  if (response.status === 401) {
    // The access token is short-lived. The api client already knows how to
    // refresh it exactly once even when several requests fail together, and
    // that guard is load-bearing (refresh-token rotation treats a second
    // concurrent refresh as token reuse). Rather than reimplement it, make a
    // cheap authenticated call through the client — it refreshes on our behalf
    // — and then replay the download with the new token.
    await api.get('/reports', signal);
    response = await send();
  }

  if (!response.ok) {
    // Errors are still JSON even though success is a file.
    const payload = (await response.json().catch(() => null)) as
      { error?: { code?: string; details?: Record<string, unknown> } } | null;
    throw new ApiError(response.status, payload?.error?.code ?? 'UNKNOWN_ERROR', payload?.error?.details);
  }
  return response;
}

/**
 * Fetch a report and save it.
 *
 * The object URL is REVOKED once the download has been handed to the browser.
 * An un-revoked object URL keeps the whole Blob (a large PDF, here) alive until
 * the page is closed, so without this every download would leak its size in
 * memory for the rest of the session.
 */
async function download(path: string, fallbackName: string, signal: AbortSignal): Promise<void> {
  const response = await fetchReport(path, signal);
  const blob = await response.blob();
  const filename = filenameFrom(response.headers.get('Content-Disposition'), fallbackName);

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  try {
    link.click();
  } finally {
    link.remove();
    // Deferred by a tick, not called inline: Safari resolves the blob URL
    // asynchronously after click(), and revoking it synchronously can abort
    // the save. Still guaranteed to run, so nothing is retained.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

// ---------------------------------------------------------------------------

export function ReportsPage() {
  const { t } = useTranslation();
  const describeError = useApiError();

  const { data: reports, isLoading, error: listError, refetch } = useQuery({
    queryKey: ['reports'],
    queryFn: () => api.get<ReportInfo[]>('/reports'),
  });

  // Best-effort: if the user cannot read frameworks, the report still works
  // (it covers everything licensed); they just cannot narrow it down.
  const { data: frameworks } = useQuery({
    queryKey: ['frameworks'],
    queryFn: () => api.get<FrameworkInfo[]>('/frameworks'),
    retry: false,
  });
  // Only licensed frameworks are offered: the report covers "each licensed
  // framework", so ticking an unlicensed one would promise content that
  // cannot exist.
  const licensed = (frameworks ?? []).filter((f) => f.licensed);

  const [selected, setSelected] = useState<string[]>([]);
  /** `${report}:${format}` of the download in flight, one per button. */
  const [busy, setBusy] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<Record<string, string>>({});

  // One controller for the page: leaving the screen cancels in-flight
  // downloads, so a slow PDF does not finish into an unmounted component or
  // pop a save dialog on whatever page the user has moved to.
  const abort = useRef(new AbortController());
  useEffect(() => {
    const controller = new AbortController();
    abort.current = controller;
    return () => controller.abort();
  }, []);

  const toggle = (key: string) =>
    setSelected((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]));

  const run = async (report: ReportInfo, format: Format) => {
    const id = `${report.key}:${format}`;
    if (busy.has(id)) return; // a double-click must not generate the PDF twice

    // The framework report takes its choice as ?frameworks=; the others take
    // ?format=. Selecting none sends no parameter, which the server reads as "all".
    const path = report.key === 'framework'
      ? `/reports/framework${qs({ frameworks: selected.join(',') })}`
      : `/reports/${report.key}${qs({ format })}`;

    setBusy((cur) => new Set(cur).add(id));
    setErrors((cur) => ({ ...cur, [report.key]: '' }));
    try {
      await download(path, `${report.key}.${format}`, abort.current.signal);
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') return; // navigated away
      setErrors((cur) => ({ ...cur, [report.key]: describeError(err) }));
    } finally {
      setBusy((cur) => {
        const next = new Set(cur);
        next.delete(id);
        return next;
      });
    }
  };

  return (
    <div className="oc-page space-y-5 animate-fade-in">
      <header>
        <h1 className="oc-h1">{t('reports.title')}</h1>
        <p className="oc-muted mt-1">{t('reports.subtitle')}</p>
      </header>

      {isLoading ? (
        <div className="grid gap-3 md:grid-cols-2">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-36" />)}
        </div>
      ) : listError ? (
        <ErrorNotice message={describeError(listError)} onRetry={() => void refetch()}
                     retryLabel={t('common.retry')} />
      ) : !reports || reports.length === 0 ? (
        <Card>
          <EmptyState icon={<IconFramework className="w-10 h-10" />} title={t('reports.none')} />
        </Card>
      ) : (
        <ul className="grid gap-3 md:grid-cols-2">
          {reports.map((report) => {
            const anyBusy = report.formats.some((f) => busy.has(`${report.key}:${f}`));
            return (
              <li key={report.key}>
                <Card className="h-full flex flex-col">
                  <h2 className="oc-h2">
                    {/* The server sends English text; a translation, when one
                        exists, wins so the page is fully localised. */}
                    {t(`reports.name.${report.key}`, { defaultValue: report.name })}
                  </h2>
                  <p className="oc-muted mt-1">
                    {t(`reports.desc.${report.key}`, { defaultValue: report.description })}
                  </p>

                  {report.key === 'framework' && licensed.length > 0 && (
                    <fieldset className="mt-3" disabled={anyBusy}>
                      <legend className="oc-label">{t('reports.frameworksLabel')}</legend>
                      <div className="flex flex-wrap gap-x-4 gap-y-1">
                        {licensed.map((f) => (
                          <label key={f.key}
                                 className="inline-flex items-center gap-2 min-h-[2.25rem] text-sm text-ink cursor-pointer">
                            <input type="checkbox" className="h-4 w-4 accent-brand"
                                   checked={selected.includes(f.key)}
                                   onChange={() => toggle(f.key)} />
                            {f.name}
                          </label>
                        ))}
                      </div>
                      <p className="text-xs text-ink-muted mt-1">{t('reports.frameworksHint')}</p>
                    </fieldset>
                  )}

                  {errors[report.key] && (
                    <div className="mt-3"><ErrorNotice message={errors[report.key]!} /></div>
                  )}

                  {/* Pushed to the bottom so buttons align across cards of
                      different description length. */}
                  <div className="mt-auto pt-4 flex flex-wrap items-center gap-2">
                    {report.formats.map((format) => {
                      const working = busy.has(`${report.key}:${format}`);
                      return (
                        <Button key={format} size="sm" variant="secondary"
                                disabled={working}
                                aria-busy={working}
                                onClick={() => void run(report, format)}>
                          {working ? (
                            <>
                              <Spinner className="h-4 w-4 mr-2" />
                              {t('reports.generating')}
                            </>
                          ) : (
                            t('reports.download', { format: format.toUpperCase() })
                          )}
                        </Button>
                      );
                    })}
                    {/* Announced to screen readers; a large PDF takes a while
                        and a silent button looks like nothing happened. */}
                    <span className="sr-only" role="status" aria-live="polite">
                      {anyBusy ? t('reports.generatingHint') : ''}
                    </span>
                    {anyBusy && (
                      <span className="text-xs text-ink-muted" aria-hidden>{t('reports.generatingHint')}</span>
                    )}
                  </div>
                </Card>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
